<script setup>
// O cartão de FAQ do kit Polaris falando a língua desta tela: traduz a resposta
// que vem da API (origem, data em epoch) e devolve os eventos no formato que a
// página já entende ({ action, id } e, para a origem, navigate { id, type }).
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { ptBR } from 'date-fns/locale';
import { dynamicTime } from 'shared/helpers/timeHelper';
import ResponseCard from 'dashboard/components-next/polaris/cards/ResponseCard.vue';

const props = defineProps({
  response: { type: Object, required: true },
  selectable: { type: Boolean, default: false },
  selected: { type: Boolean, default: false },
});

const emit = defineEmits(['action', 'select', 'navigate']);

const { t, locale } = useI18n();

const dateLocale = computed(() =>
  locale.value === 'pt_BR' ? ptBR : undefined
);

// De onde a resposta saiu, como o cartão antigo dizia: o documento, a pessoa que
// a escreveu ou a conversa ("Conversa #N").
const source = computed(() => {
  const { documentable } = props.response;
  if (!documentable) return '';
  if (documentable.type === 'Conversation') {
    return t('POLARIS.RESPONSES.DOCUMENTABLE.CONVERSATION', {
      id: documentable.display_id,
    });
  }
  if (documentable.type === 'User') return documentable.available_name || '';
  return documentable.name || documentable.external_link || '';
});

// Quando a origem é uma conversa, ela abre a conversa, como no cartão antigo. O
// cartão do kit fica igual ao do kit (a origem é um texto no rodapé, o primeiro
// <p> dele), então o clique é pego aqui, no invólucro.
const fromConversation = computed(
  () => props.response.documentable?.type === 'Conversation'
);
const SOURCE_SELECTOR = '[data-slot="card-footer"] > p:first-of-type';
const onClick = event => {
  if (!fromConversation.value || !event.target.closest(SOURCE_SELECTOR)) return;
  const { id, type } = props.response.documentable;
  emit('navigate', { id, type });
};

const updated = computed(() => {
  const at = props.response.updated_at || props.response.created_at;
  return at
    ? t('POLARIS.CARDS.UPDATED_AT', { time: dynamicTime(at, dateLocale.value) })
    : '';
});
</script>

<template>
  <div
    :class="
      fromConversation &&
      '[&_[data-slot=card-footer]>p:first-of-type]:cursor-pointer [&_[data-slot=card-footer]>p:first-of-type:hover]:underline'
    "
    @click="onClick"
  >
    <ResponseCard
      :question="response.question"
      :answer="response.answer"
      :status="response.status === 'pending' ? 'pending' : 'approved'"
      :source="source"
      :updated="updated"
      :selectable="selectable"
      :selected="selected"
      @action="({ action }) => emit('action', { action, id: response.id })"
      @select="emit('select', response.id)"
    />
  </div>
</template>
